import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApiKeysList } from './hooks/useApiKeysList';
import { ApiKeyService } from './services/ApiKeyService';
import type { ApiKeysAdminProps } from './types/apiKey.types';

const SCOPES = ['syn_arrenda'] as const;

function formatFecha(value: string | null) {
    if (!value) {
        return '-';
    }

    const fecha = new Date(value);
    return Number.isNaN(fecha.getTime()) ? value : fecha.toLocaleString('es-MX');
}

const ApiKeysAdmin: React.FC<ApiKeysAdminProps> = ({ canAdd, canRevoke }) => {
    const { rows, isLoading, error, reload } = useApiKeysList();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [nombreCliente, setNombreCliente] = useState('');
    const [scope, setScope] = useState<string>(SCOPES[0]);
    const [generatedKey, setGeneratedKey] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [revokingId, setRevokingId] = useState<number | null>(null);

    useEffect(() => {
        document.body.style.overflow = showCreateModal || showResultModal ? 'hidden' : 'auto';
    }, [showCreateModal, showResultModal]);

    const openCreateModal = () => {
        setNombreCliente('');
        setScope(SCOPES[0]);
        setFormError(null);
        setShowCreateModal(true);
    };

    const handleCreate = async (event: React.FormEvent) => {
        event.preventDefault();
        setFormError(null);
        setIsSubmitting(true);

        try {
            const response = await ApiKeyService.crear({
                nombre_cliente: nombreCliente.trim(),
                scope,
            });

            if (response.status !== 'success' || !response.data?.api_key) {
                throw new Error(response.message || 'No se pudo generar la API Key.');
            }

            setGeneratedKey(response.data.api_key);
            setShowCreateModal(false);
            setShowResultModal(true);
            await reload();
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'No se pudo generar la API Key.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRevoke = async (row: { clave_api_key: number; nombre_cliente: string }) => {
        if (!window.confirm(`¿Revocar la API Key de "${row.nombre_cliente}"?`)) {
            return;
        }

        setRevokingId(row.clave_api_key);

        try {
            const response = await ApiKeyService.revocar(row.clave_api_key);

            if (response.status !== 'success') {
                throw new Error(response.message || 'No se pudo revocar la API Key.');
            }

            await reload();
        } catch (err) {
            window.alert(err instanceof Error ? err.message : 'No se pudo revocar la API Key.');
        } finally {
            setRevokingId(null);
        }
    };

    const handleCopy = async () => {
        if (!generatedKey) {
            return;
        }

        await navigator.clipboard.writeText(generatedKey);
    };

    return (
        <div className="card">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0 fw-bold">API Keys</h5>
                    {canAdd && (
                        <button type="button" className="btn btn-success" onClick={openCreateModal}>
                            <i className="far fa-plus" /> Generar API Key
                        </button>
                    )}
                </div>

                {isLoading && <p className="text-muted mb-0">Cargando...</p>}
                {!isLoading && error && <div className="alert alert-danger mb-0">{error}</div>}

                {!isLoading && !error && (
                    <div className="table-responsive">
                        <table className="table table-striped mb-0">
                            <thead>
                                <tr>
                                    <th className="cs_icon" />
                                    <th>Cliente</th>
                                    <th>Scope</th>
                                    <th>Estado</th>
                                    <th>Fecha alta</th>
                                    <th>Usuario alta</th>
                                    <th>Fecha revocación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={7}>No hay API Keys registradas.</td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr key={row.clave_api_key}>
                                            <td className="cs_icon">
                                                {row.activo && canRevoke && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline"
                                                        disabled={revokingId === row.clave_api_key}
                                                        onClick={() => void handleRevoke(row)}
                                                    >
                                                        <i className="far fa-ban" />
                                                    </button>
                                                )}
                                            </td>
                                            <td>{row.nombre_cliente}</td>
                                            <td>{row.scope}</td>
                                            <td>
                                                {row.activo ? (
                                                    <span className="badge bg-success">Activa</span>
                                                ) : (
                                                    <span className="badge bg-secondary">Revocada</span>
                                                )}
                                            </td>
                                            <td>{formatFecha(row.fecha_alta)}</td>
                                            <td>{row.usuario_alta || '-'}</td>
                                            <td>{formatFecha(row.fecha_revocacion)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showCreateModal && createPortal(
                <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <form className="modal-content" onSubmit={(event) => void handleCreate(event)}>
                            <div className="modal-header">
                                <h5 className="modal-title">Generar API Key</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)} />
                            </div>
                            <div className="modal-body">
                                {formError && <div className="alert alert-danger">{formError}</div>}
                                <div className="form-group mb-3">
                                    <label htmlFor="api-key-nombre-cliente">Nombre del cliente</label>
                                    <input
                                        id="api-key-nombre-cliente"
                                        type="text"
                                        className="form-control"
                                        value={nombreCliente}
                                        maxLength={100}
                                        required
                                        onChange={(event) => setNombreCliente(event.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="api-key-scope">Scope</label>
                                    <select
                                        id="api-key-scope"
                                        className="form-select"
                                        value={scope}
                                        required
                                        onChange={(event) => setScope(event.target.value)}
                                    >
                                        {SCOPES.map((item) => (
                                            <option key={item} value={item}>{item}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-success" disabled={isSubmitting}>
                                    {isSubmitting ? 'Generando...' : 'Generar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body,
            )}

            {showResultModal && createPortal(
                <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">API Key generada</h5>
                                <button type="button" className="btn-close" onClick={() => setShowResultModal(false)} />
                            </div>
                            <div className="modal-body">
                                <p className="text-danger">Guarda esta llave ahora. No podrá volver a visualizarse.</p>
                                <div className="input-group">
                                    <input type="text" className="form-control" value={generatedKey} readOnly />
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => void handleCopy()}>
                                        Copiar
                                    </button>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-primary" onClick={() => setShowResultModal(false)}>
                                    Entendido
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default ApiKeysAdmin;
